import { IDeepApi, IDeepApiCli } from "@api/api.int";
import { useToast } from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DeepAuthContext } from "../features/auth/DeepAuth";

export function generateKey() {
	return '' + (new Date().getTime());
}

//const API_BASE = 'https://d3rfl77i7pfstf.cloudfront.net'; //prod
//const API_BASE = 'https://api.vizvedok.hu';
//const API_BASE = 'https://d2lt2x7oefh0o.cloudfront.net'; // dev
console.log('STARTED WITH API:', process.env.REACT_APP_API_BASE_URL);
export const API_BASE = process.env.REACT_APP_API_BASE_URL;

export function useDeepAuth() {
	return useContext(DeepAuthContext);
}

export function useVVApi() {
	const authCtx = useContext(DeepAuthContext);
	const toast = useToast();

	// ---------------------------------------
	const apiFetch = useCallback(async function <TApi extends IDeepApiCli>(req: TApi['request'], init?: RequestInit): Promise<TApi['response']['payload']> {
		if (req.params) {
			const arr = req.url.split('/');
			for (let key of Object.keys(req.params)) {
				const ix = arr.indexOf(':' + key);
				if (ix < 0)
					throw `Param not found in fetch: ${key}`;
				arr[ix] = req.params[key];
			}
			req.url = arr.join('/');
		}
		let url = API_BASE + (req.url.startsWith('/') ? '' : '/') + req.url;
		if (req.query) {
			let qArr = new Array<string>();
			for (let name in req.query) 
				qArr.push(`${name}=${encodeURIComponent(req.query[name])}`);
			url += '?' + qArr.join('&');
		}
		const deepToken = authCtx.getToken(); //btoa(JSON.stringify({ signedUser: authCtx.signedUser, signature: authCtx.signature, }));
		init = Object.assign({}, init, {});
		init.headers = Object.assign({}, init.headers
			, {
				'X-Deep-Token': deepToken
			}
		);
		init.method = req.method;
		if (req.payload)
			init.body = JSON.stringify(req.payload);


		//console.log('----->> before', url);
		const response = await fetch(url, init);
		//console.log('----->> response', response);
		let resp: any = {};
		let jsonError: any;
		try {
			resp = await response.json();
		} catch (ex) {
			jsonError = ex;
		}
		if (response.ok) {
			if (jsonError)
				throw jsonError;
			return resp;
		}
		if (resp?.publicMessage)
			throw response.status + ' | ' + resp.publicMessage;
		if (resp?.error) {
			throw response.status + ' | ' + resp.error;
		} else {
			throw response.status + ' | ' + response.statusText;
		}
	}, [authCtx]);



	const apiFetchToast = useCallback(async function <TApi extends IDeepApiCli>(req: TApi['request'], init?: RequestInit): Promise<TApi['response']['payload']> {
		//console.log('toast....')
		let responsePromise = apiFetch<TApi>(req, init);
		toast.promise(responsePromise, {
			success: { title: 'Sikerült', duration: 0 },
			error: (ex) => ({ title: 'Hiba', description: "" + ex }),
			loading: { title: 'Dolgozunk...' },
		})

		return await responsePromise.then();
	}, [toast, apiFetch]);




	return useMemo(() => ({
		fetch: apiFetch,
		fetchToast: apiFetchToast,
	}), [apiFetch, apiFetchToast]);
}

// ===============================================================================
export interface IDeepEndpointState {
	isLoading: boolean,
	isFirstLoaded: boolean,
	error?: string,
}

export function useDeepEndpoint<TApi extends IDeepApiCli>(url: TApi['request']['url'], params: TApi['request']['params']
	, callback: (
		data: TApi['response']['payload']
	) => void
	, init?: RequestInit): IDeepEndpointState {

	const api = useVVApi();

	//------ should be function ------
	let fullUrl = url;
	if (params) {
		const arr = fullUrl.split('/');
		for (let key of Object.keys(params)) {
			const ix = arr.indexOf(':' + key);
			if (ix < 0)
				throw `Param not found in fetch: ${key}`;
			arr[ix] = params[key];
		}
		fullUrl = arr.join('/');
	}
	fullUrl = API_BASE + (fullUrl.startsWith('/') ? '' : '/') + fullUrl;
	//------ should be function ------


	const [state, setState] = useState<IDeepEndpointState>({
		isLoading: true,
		isFirstLoaded: false,

	});

	useEffect(() => {
		if ((window as any).dvPreload && (window as any).dvPreload[fullUrl]) {
			callback((window as any).dvPreload[fullUrl].data);
			setState({
				isFirstLoaded: true,
				isLoading: false,
			})
		}

		(async () => {
			try {
				const resp = await api.fetch<TApi>({
					url, method: 'GET', params
				});
				callback(resp);
				setState({
					isFirstLoaded: true,
					isLoading: false,
				});
			} catch (ex) {
				setState({
					isFirstLoaded: false,
					isLoading: false,
					error : ''+ex
				});
			}
		})();



	}, [fullUrl]);

	return state;

}

